/**
 * The Claude models offered by the chat's model switcher.
 *
 * Mirrors the backend allow list in `app/constants/aiAssistantModels.ts`. The
 * backend is what actually enforces this — it re-resolves whatever id arrives
 * and falls back to the default — so a stale entry here degrades to the
 * default rather than breaking a message.
 */

export interface AiModelOption {
	id: string;
	// What the closed dropdown shows — the family plus its version, short
	// enough for a trigger button.
	label: string;
	// What the open dropdown shows. The full product name, so the version is
	// unambiguous when comparing the three side by side.
	fullLabel: string;
	// One short line under the name. Kept to a handful of words so the menu
	// stays narrow — a full sentence stretched it to nearly 400px.
	description: string;
}

export const AI_MODEL_OPTIONS: AiModelOption[] = [
	{
		id: 'claude-opus-5',
		label: 'Opus 5',
		fullLabel: 'Claude Opus 5',
		description: 'Deepest analysis, slower.',
	},
	{
		id: 'claude-sonnet-5',
		label: 'Sonnet 5',
		fullLabel: 'Claude Sonnet 5',
		description: 'Balanced speed and depth.',
	},
	{
		id: 'claude-haiku-4-5',
		label: 'Haiku 4.5',
		fullLabel: 'Claude Haiku 4.5',
		description: 'Fastest, for quick lookups.',
	},
];

export const findAiModel = (id: string): AiModelOption =>
	AI_MODEL_OPTIONS.find((option) => option.id === id) || AI_MODEL_OPTIONS[0];

// Haiku — fastest and cheapest — is the default for anyone who hasn't picked a
// model; the switcher still offers Opus 5 and Sonnet 5. Mirrors the backend
// allow list's DEFAULT_AI_ASSISTANT_MODEL.
export const DEFAULT_AI_MODEL_ID =
	AI_MODEL_OPTIONS.find((option) => option.id === 'claude-haiku-4-5')?.id ?? AI_MODEL_OPTIONS[0].id;

const STORAGE_KEY = 'cap.ai.model';

// The choice is a preference, not conversation state, so it outlives both the
// thread and the page. localStorage can throw outright in a locked-down
// browser, hence the guards — a failure here must not stop the chat loading.
export const readStoredAiModel = (): string => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored && AI_MODEL_OPTIONS.some((option) => option.id === stored)) {
			return stored;
		}
	} catch {
		// Private mode, blocked site data — fall through to the default.
	}
	return DEFAULT_AI_MODEL_ID;
};

export const storeAiModel = (modelId: string): void => {
	try {
		localStorage.setItem(STORAGE_KEY, modelId);
	} catch {
		// Not being able to remember the choice is survivable; the switcher
		// still works for the life of the page.
	}
};
